import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AppLayout,
  BreadcrumbGroup,
  Header,
  ContentLayout,
  Spinner,
} from '@amzn/awsui-components-react';
import { AttendanceWizard, FIRST_PAGE } from '../wizard/attendanceWizard';
import { AttendanceModal } from '../leaveModal';
import { BulkUploadModal } from '../bulkUpload/bulkUploadModal';

import { SearchTable } from '../wizard/searchTable';
import { AttendeeTable } from '../csvAttendance/attendeeTable';
import { EditEventInfo } from '../editEvent/editEventInfo';
import { AdditionalInfo } from '../editEvent/additionalInfo';
import { SpaceBetween } from '@amzn/awsui-components-react';
import { CoordinatorAttendeeTable } from './coordinatorAttendeeTable';
import { publishKatalMetric } from '../../katalAnalytics';
import { useWizardStrings } from '../../../hooks/localization/wizardStrings';
import { useUrlPage } from '../../../hooks/attendance/urlPage';

import { useShiftAttendance } from '../../../hooks/attendance/attendanceMutation';

import { useAttendeeSessionStorage } from '../../../hooks/attendance/attendeeSessionStorage';
import { useParticipantSessionStorage } from '../../../hooks/attendance/participantSessionStorage';
import { useParticipants } from '../../../hooks/attendance/useParticipants';
import { useUser } from '../../../hooks/user';
import { getValueFromSearchParam } from '../../../utils/url';

import { ShiftAttendanceHeader } from './shiftAttendanceHeader';

import { useEditableEventInfo, EditableInfoField } from '../../../hooks/events/editableEventInfo';
import { AttendanceStatus } from '@amzn/red-velvet-api';
import { featureIsEnabled, useFeatures } from '../../../hooks/features';
import { TRACK_ATTENDANCE_BULK_UPLOAD_SINGLE_SHIFT } from '../../../utils/constants';
import { useBulkParticipantSessionStorage } from '../../../hooks/attendance/bulkParticipantSessionStorage';

function EventDetailValidationSummary({
  errors,
}: {
  errors: { [s in EditableInfoField]?: string };
}) {
  const namespaces = ['attendance', 'translation'];
  const { t } = useTranslation(namespaces);

  const errorKeys = (Object.keys(errors) as Array<EditableInfoField>).filter((key) => errors[key]);

  return (
    <div>
      <span>
        {t('shiftAttendanceWizard.eventDetails.validationErrors', { count: errorKeys.length })}
      </span>
      <ul>
        {errorKeys.map((key) => (
          <li key={key}>{errors[key]}</li>
        ))}
      </ul>
    </div>
  );
}

// Based on Golden Path in https://code.amazon.com/packages/RedVelvetReactWebsite/blobs/mainline/--/src/components/attendance/csvAttendance/csvAttendance.tsx
export function CoordinatorAttendancePage() {
  const namespaces = ['attendance', 'translation'];
  const { t } = useTranslation(namespaces);
  const user = useUser();
  const features = useFeatures();

  const [searchParams, setSearchParams] = useSearchParams();

  const eventId = getValueFromSearchParam(searchParams, 'eventId', '');
  const shiftId = getValueFromSearchParam(searchParams, 'shiftId', '');

  const isBulkUploadSingleShiftEnabled =
    featureIsEnabled(TRACK_ATTENDANCE_BULK_UPLOAD_SINGLE_SHIFT, features, searchParams) &&
    // shiftId can be undefined for Multi-shift bulk upload (future feature)
    // To enable only Single-shift and prevent Multi-shift access, we check:
    //   - Both eventId AND shiftId exist: regular shift tracking
    //   - Both eventId AND shiftId absent: unlisted event shift tracking
    // This blocks the invalid state until Multi-shift bulk upload is implemented (eventId without shiftId) to prevent URL manipulation
    ((eventId && shiftId) || (!eventId && !shiftId));

  const navigate = useNavigate();
  const [modalVisible, setModalVisible] = useState(false);
  const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
  const [navigateTo, setNavigateTo] = useState('#');

  const { participants, resetParticipants } = useParticipantSessionStorage();
  const { aliases, resetAttendees } = useAttendeeSessionStorage();
  const { bulkParticipants, resetBulkParticipants } = useBulkParticipantSessionStorage();

  // Get attendee data and loading state for auto-open logic
  const attendeeCollection = useParticipants(
    ['attendance', 'translation'],
    'coordinatorAttendeeTable',
    eventId,
    shiftId,
    100,
    true
  );
  const { items: attendeeItems, isLoading: attendeesLoading } = attendeeCollection;

  const { page, setPage, updateParams: updatePageParams } = useUrlPage(FIRST_PAGE);
  // Note - these have to be passed down to the step from the parent so that we can validate the changes when a user clicks next step
  // and send the values to the API when the user clicks submit
  const {
    editableInfo,
    defaultsFromApi,
    event,
    shift,
    shifts,
    hasChanges: eventChanges,
    isFetched,
    eventWarnings,
    eventErrors,
    validateFields,
    updateEditableInfoFields,
    saveEventToUrl,
    updateParams: updateEventParams,
  } = useEditableEventInfo();

  // If we have data saved in the url, we want to confirm with the user before navigating away
  const hasChanges =
    eventChanges ||
    Object.keys(participants).length !== 0 ||
    Object.keys(bulkParticipants).length !== 0 ||
    aliases.length !== 0;

  const onNavigateAway = (href: string) => {
    if (hasChanges) {
      setModalVisible(true);
      setNavigateTo(href);
    } else {
      resetAttendees();
      resetParticipants();
      navigate(href);
    }
  };
  const leave = () => {
    resetAttendees();
    resetParticipants();
    navigate(navigateTo);
  };

  const wizardStrings = useWizardStrings('shiftAttendanceWizard', namespaces);

  const attendanceMutation = useShiftAttendance(user.userId, 25);

  const eventDetailsSteps = [
    {
      title: t('shiftAttendanceWizard.stepTitles.eventDetails'),
      content: isFetched ? (
        <SpaceBetween direction="vertical" size="l">
          <EditEventInfo
            editableInfo={editableInfo}
            updateEditableInfoFields={updateEditableInfoFields}
            eventWarnings={eventWarnings}
            eventErrors={eventErrors}
          />
          <AdditionalInfo
            editableInfo={editableInfo}
            updateEditableInfoFields={updateEditableInfoFields}
            eventWarnings={eventWarnings}
            eventErrors={eventErrors}
          />
        </SpaceBetween>
      ) : (
        <SpaceBetween direction="vertical" size="l">
          <Spinner />
          <span>{t('fullEvent.loadingEvent')}</span>
        </SpaceBetween>
      ),
      errorText:
        (Object.keys(eventErrors) as Array<EditableInfoField>).filter((key) => eventErrors[key])
          .length > 0 ? (
          <EventDetailValidationSummary errors={eventErrors} />
        ) : null,
    },
  ];

  // Auto-open modal when on Track Attendance step with 0 attendees (after API loads)
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  // On mounting the Track Attendance wizard, clean up any previous bulk-participants session storage
  // TODO: finalize whether to clean bulk-participants on page refresh after discuss with Gil SIM: cce-6071
  useEffect(() => {
    resetBulkParticipants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      isBulkUploadSingleShiftEnabled &&
      page === 1 &&
      !attendeesLoading &&
      attendeeItems.length === 0 &&
      !hasAutoOpened
    ) {
      publishKatalMetric('attendance_bulkUploadModalOpened', 'autoOpenSingleShiftTracking');
      setBulkUploadModalVisible(true);
      setHasAutoOpened(true);
    }
  }, [page, isBulkUploadSingleShiftEnabled, attendeesLoading, attendeeItems.length, hasAutoOpened]);

  const steps = [
    ...eventDetailsSteps,
    {
      title: t('shiftAttendanceWizard.stepTitles.trackAttendance'),
      content: isBulkUploadSingleShiftEnabled ? (
        <AttendeeTable
          onAddAttendees={() => {
            publishKatalMetric('attendance_bulkUploadModalOpened', 'singleShiftTracking');
            setBulkUploadModalVisible(true);
          }}
        />
      ) : (
        <SpaceBetween direction="vertical" size="l">
          <AttendeeTable />
          <SearchTable participants={[]} setParticipants={() => []} onUpdate={() => undefined} />
        </SpaceBetween>
      ),
      description: t('shiftAttendanceWizard.addUsersInstructions'),
    },
    {
      title: t('shiftAttendanceWizard.stepTitles.trackTime'),
      content: <CoordinatorAttendeeTable />,
      description: t('shiftAttendanceWizard.trackTimeInstructions'),
    },
    {
      title: t('shiftAttendanceWizard.stepTitles.submit'),
      content: <CoordinatorAttendeeTable isImmutable />,
      errorText: attendanceMutation.error ? JSON.stringify(attendanceMutation.error) : null,
    },
  ];

  return (
    <AppLayout
      footerSelector=".footer"
      navigationHide={true}
      toolsHide={true}
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: t('breadcrumbs.home', { ns: 'translation' }), href: '/' },
            { text: t('breadcrumbs.coordinatorActivity'), href: '/coordinator-activity' },
            { text: t('breadcrumbs.attendanceSheet'), href: '#' },
          ]}
          onFollow={(event) => {
            onNavigateAway(event.detail.href);
            event.preventDefault();
          }}
        />
      }
      content={
        <ContentLayout
          header={
            <div className="page-content-header">
              <Header variant="h1">{t('pageTitle.attendanceSheet')}</Header>
            </div>
          }
        >
          {/* Event and shift HAVE to be passed down through the component chain here, so that they can pick up state changes in the event info form */}
          {(eventId && shiftId) || page !== 0 ? (
            <ShiftAttendanceHeader event={event} shift={shift} isFetched={isFetched} />
          ) : null}
          <AttendanceWizard
            i18nStrings={{
              ...wizardStrings,
              // Next button should match the title of the next step
              nextButton: t([`shiftAttendanceWizard.nextButton`], {
                title: steps[page + 1] ? steps[page + 1].title : t([`wizard.nextButton`]),
              }),
            }}
            isLoadingNextStep={attendanceMutation.isPending}
            steps={steps}
            onCancel={() => {
              onNavigateAway('/coordinator-activity?tab=past');
            }}
            onNavigate={({ detail }) => {
              if (page === 0) {
                if (detail.requestedStepIndex > page && !validateFields(editableInfo)) {
                  // Only update the event in the url and DO NOT change pages
                  saveEventToUrl();
                } else {
                  // Update both the page and event params in the url at the same time
                  setSearchParams((params) => {
                    const paramsWithNewPage = updatePageParams(params, detail.requestedStepIndex);
                    return updateEventParams(paramsWithNewPage, editableInfo, defaultsFromApi);
                  });
                }
              } else {
                setPage(detail.requestedStepIndex);
              }
            }}
            onSubmit={() => {
              attendanceMutation.mutate(
                {
                  event,
                  shift,
                  attendanceInputs: {
                    attendanceRequests: Array.from(
                      // Currently, searched amazonians are added and stored into "aliases" (CoordinatorTracking feature) and "bulk-participants" (BulkUpload feature)
                      // Any user edits to added attendee (existing or newly added)'s attendance status will be reflected in "participants"
                      // Therefore, we want to grab all the aliases from 3 sources to process the attendance requests
                      new Set([
                        ...Object.keys(participants),
                        ...Object.keys(bulkParticipants),
                        ...aliases,
                      ])
                    ).map((alias) => {
                      const attendanceData = participants[alias];

                      // If the alias is added by coordinator through CoordinatorTracking or BulkUpload, we assign the alias's attendance to ATTENDED
                      const attendanceAddedByDefault =
                        aliases.includes(alias) || Object.keys(bulkParticipants).includes(alias)
                          ? 'ATTENDED'
                          : 'DID_NOT_ATTEND';

                      // If there are user edits to any attendee (existing or newly added), we use those values over the default attendance
                      const attendanceStatus =
                        attendanceData?.attendance || attendanceAddedByDefault;

                      return {
                        alias,
                        eventId: eventId,
                        shiftId: shiftId,
                        minutes:
                          attendanceStatus === AttendanceStatus.ATTENDED
                            ? attendanceData?.trackedTime || shift.shiftLength
                            : 0,
                        attendanceStatus: attendanceStatus as AttendanceStatus,
                        $metadata: {},
                      };
                    }),
                  },
                },
                {
                  onSuccess: () => {
                    resetAttendees();
                    resetParticipants();
                    navigate('/coordinator-activity?tab=past&status=success');
                  },
                }
              );
            }}
          />
          <AttendanceModal
            hasChanges={hasChanges}
            onConfirm={leave}
            visible={modalVisible}
            onDismiss={() => setModalVisible(false)}
          />
          {isBulkUploadSingleShiftEnabled && page !== 0 && (
            <BulkUploadModal
              visible={bulkUploadModalVisible}
              onDismiss={() => setBulkUploadModalVisible(false)}
              event={event}
              shifts={shifts}
              shiftId={shiftId || undefined}
            />
          )}
        </ContentLayout>
      }
      contentType="wizard"
    />
  );
}
